import { useState } from "react";
import { UsersThree, Minus, Plus } from "@phosphor-icons/react";

/**
 * SeatPicker — used on Signup and Settings → Manage Seats.
 *
 * Renders a 1-10 dropdown plus an "Other" option that swaps to a free-text input
 * for businesses needing >10 seats. Total monthly price updates live.
 *
 * Props:
 *  - seats         (number)        — current selected seat count
 *  - onChange(n)   (fn)            — called with the new integer seat count
 *  - pricePerSeat  (number)        — USD/seat/mo
 *  - minSeats      (number, opt)   — minimum allowed (defaults to 1). Settings sets this to seats_used.
 *  - label         (string, opt)   — heading text
 *  - data-testid prefix is "seats-picker"
 */
export default function SeatPicker({ seats, onChange, pricePerSeat, minSeats = 1, label = "How many seats?" }) {
  const isPresetValue = seats >= 1 && seats <= 10;
  const [showCustom, setShowCustom] = useState(!isPresetValue);
  const monthly = Math.max(seats, 0) * pricePerSeat;

  const handleDropdown = (v) => {
    if (v === "custom") { setShowCustom(true); onChange(Math.max(11, seats)); return; }
    setShowCustom(false);
    onChange(Number(v));
  };

  return (
    <div className="border border-[var(--border)] p-5 bg-[var(--bg-sidebar)]" data-testid="seats-picker">
      <div className="flex items-center gap-2.5 mb-1">
        <UsersThree size={18} weight="fill" className="text-[var(--accent)]" />
        <label className="label !mb-0">{label}</label>
      </div>
      <div className="text-[12px] text-[var(--text-secondary)] mb-3">
        Each seat = 1 team member with their own phone numbers, voicemail, SMS inbox, and call history. The account owner uses 1 seat.
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-stretch">
        {!showCustom ? (
          <select
            className="input flex-1"
            value={seats <= 10 ? String(seats) : "custom"}
            onChange={(e) => handleDropdown(e.target.value)}
            data-testid="seats-dropdown"
          >
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n} disabled={n < minSeats}>
                {n} {n === 1 ? "seat" : "seats"}{n < minSeats ? " (below current usage)" : ""}
              </option>
            ))}
            <option value="custom">More than 10 — enter custom amount…</option>
          </select>
        ) : (
          <div className="flex items-center gap-2 flex-1">
            <button
              type="button"
              onClick={() => onChange(Math.max(minSeats, seats - 1))}
              disabled={seats <= minSeats}
              className="px-3 py-2 border border-[var(--border)] bg-white hover:bg-[var(--bg-sidebar)] disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="seats-decr"
              aria-label="Decrease seats"
            >
              <Minus size={14} />
            </button>
            <input
              type="number"
              className="input text-center flex-1 font-mono font-semibold"
              min={minSeats}
              max={10000}
              value={seats}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (Number.isNaN(n)) return;
                onChange(Math.max(minSeats, Math.min(10000, n)));
              }}
              data-testid="seats-custom-input"
            />
            <button
              type="button"
              onClick={() => onChange(Math.min(10000, seats + 1))}
              className="px-3 py-2 border border-[var(--border)] bg-white hover:bg-[var(--bg-sidebar)]"
              data-testid="seats-incr"
              aria-label="Increase seats"
            >
              <Plus size={14} />
            </button>
            <button
              type="button"
              onClick={() => { setShowCustom(false); onChange(Math.min(10, Math.max(minSeats, seats))); }}
              className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text-primary)] underline px-2 whitespace-nowrap"
              data-testid="seats-back-to-dropdown"
            >
              back to list
            </button>
          </div>
        )}

        <div className="border border-[var(--border)] bg-white px-4 py-2 flex flex-col items-end justify-center min-w-[160px]" data-testid="seats-total">
          <div className="font-mono text-[10px] tracking-widest text-[var(--text-muted)]">MONTHLY TOTAL</div>
          <div className="font-display font-extrabold text-[20px] leading-none mt-0.5">
            ${monthly.toLocaleString()}<span className="text-[12px] font-mono text-[var(--text-secondary)] font-normal">/mo</span>
          </div>
          <div className="font-mono text-[10px] text-[var(--text-muted)] mt-0.5">
            {seats} × ${pricePerSeat}
          </div>
        </div>
      </div>
    </div>
  );
}
